/**
 * P2-10 一键转客户弹框
 */
import { useState } from 'react';
import { Modal, Select, Input, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn } from './ui';

interface Props {
  open: boolean;
  customerId: number;
  customerName: string;
  currentOwnerId?: number | null;
  onClose: () => void;
}

export function TransferCustomerModal({ open, customerId, customerName, currentOwnerId, onClose }: Props) {
  const qc = useQueryClient();
  const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const { data: users } = useQuery({
    queryKey: ['users-list-for-transfer'],
    queryFn: () => $.get<any>('/users?pageSize=200'),
    enabled: open,
  });
  // 巡检#7+8：主管也做业绩、可被转入 → 候选含 sales + manager
  const opts = (users?.items ?? users ?? []).filter((u: any) =>
    ['sales', 'manager'].includes(u.role) && u.isActive && Number(u.id) !== Number(currentOwnerId)
  ).map((u: any) => ({ value: Number(u.id), label: `${u.realName} (#${u.id}${u.role === 'manager' ? ' · 主管' : ''})` }));

  const transfer = useMutation({
    mutationFn: () => $.post(`/customers/${customerId}/transfer`, { newOwnerId, reason }),
    onSuccess: () => {
      message.success('已转让');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] }); // 评审#H7：归一 string key
      onClose();
      setNewOwnerId(null); setReason('');
    },
    // ux1：补 onError 避免转让失败时静默（提交钮已 disabled=isPending 防重，无需再加防护）
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '转让失败'),
  });

  return (
    <Modal
      title={`转客户 · ${customerName}`}
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               onClick={() => transfer.mutate()}
               disabled={!newOwnerId || transfer.isPending}>
            {transfer.isPending ? '处理中…' : '确认转让'}
          </Btn>
        </div>
      }
    >
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-3)' }}>
        转客户给同事会让对方立刻接管，原负责人将不再看到此客户。
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>新负责人</div>
        <Select
          value={newOwnerId ?? undefined}
          onChange={(v) => setNewOwnerId(Number(v))}
          options={opts}
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) =>
            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          placeholder="选择销售员"
        />
      </div>
      <div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>转让原因（可选）</div>
        <Input.TextArea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="例如：客户更需要 X X 的专业经验"
        />
      </div>
    </Modal>
  );
}
